import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';

const AuthPage: React.FC = () => {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();

  const onLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        setError(error.message);
      } else {
        setMessage('Signed in successfully');
        // navigate to home or intended page
        navigate('/');
      }
    } catch (err: any) {
      setError(err?.message || String(err));
    } finally {
      setLoading(false);
    }
  };

  const onRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({ email, password, options: { data: { full_name: name } } });
      if (error) {
        setError(error.message);
      } else {
        setMessage('Registration successful. Check your email to confirm your account (if enabled).');
        // optionally navigate or clear fields
        setEmail('');
        setPassword('');
        setName('');
        setMode('login');
      }
    } catch (err: any) {
      setError(err?.message || String(err));
    } finally {
      setLoading(false);
    }
  };

  const onSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-bg">
      <div className="w-full max-w-md glass card p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-sm text-gray-500">Welcome to</div>
            <div className="text-2xl font-semibold">ProjectBQO</div>
          </div>
          <div className="text-sm text-gray-500">{mode === 'login' ? 'Sign in' : 'Create account'}</div>
        </div>

        {message && <div className="mb-4 p-3 rounded-md bg-green-50 text-green-800">{message}</div>}
        {error && <div className="mb-4 p-3 rounded-md bg-red-50 text-red-800">{error}</div>}

        {mode === 'login' ? (
          <form onSubmit={onLogin} className="space-y-4">
            <div>
              <label className="block text-sm text-slate-600">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full rounded-xl border border-gray-200 bg-white/60 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-600">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 block w-full rounded-xl border border-gray-200 bg-white/60 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full text-white py-2 px-4 rounded-full ${loading ? 'bg-gray-300' : 'bg-accent hover:bg-accent-600'}`}
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </button>

            <div className="text-center text-sm text-muted">
              <button type="button" onClick={() => setMode('register')} className="underline">Create an account</button>
            </div>
          </form>
        ) : (
          <form onSubmit={onRegister} className="space-y-4">
            <div>
              <label className="block text-sm text-slate-600">Full name</label>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 block w-full rounded-xl border border-gray-200 bg-white/60 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-600">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full rounded-xl border border-gray-200 bg-white/60 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-600">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 block w-full rounded-xl border border-gray-200 bg-white/60 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full text-white py-2 px-4 rounded-full ${loading ? 'bg-gray-300' : 'bg-accent hover:bg-accent-600'}`}
            >
              {loading ? 'Creating…' : 'Create account'}
            </button>

            <div className="text-center text-sm text-muted">
              <button type="button" onClick={() => setMode('login')} className="underline">Already have an account? Sign in</button>
            </div>
          </form>
        )}

        <div className="mt-6 text-center text-sm text-gray-500">
          <button onClick={onSignOut} className="text-xs underline">Sign out (if signed in)</button>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
